import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// While REACT_APP_AUTO_LOGIN=true the app signs in as the demo user by itself (no login screen).
// Set it to anything else (or remove it) and rebuild to bring the login page back.
const AUTO_LOGIN = process.env.REACT_APP_AUTO_LOGIN === 'true';
const DEMO_EMAIL = 'demo@blueskies.com';
const DEMO_PASSWORD = 'password123';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Sign in as the demo user when auto-login is on. Runs before the app leaves its loading
  // state, so the router never bounces through /login. On failure the login page is shown.
  const autoLogin = async () => {
    if (!AUTO_LOGIN) return;
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: DEMO_EMAIL, password: DEMO_PASSWORD }),
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      localStorage.setItem('user', JSON.stringify(data));
      setUser(data);
    } catch (err) {
      console.error('Auto login failed:', err);
    }
  };

  // Check existing session on mount
  const checkAuth = useCallback(async () => {
    const storedUser = AUTO_LOGIN ? null : localStorage.getItem('user');
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        setUser(userData);
        setLoading(false);
        return;
      } catch (e) {
        localStorage.removeItem('user');
      }
    }

    try {
      const response = await fetch(`${API_URL}/api/auth/me`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const userData = await response.json();
        localStorage.setItem('user', JSON.stringify(userData));
        setUser(userData);
      } else {
        setUser(null);
        localStorage.removeItem('user');
        await autoLogin();
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      localStorage.removeItem('user');
      await autoLogin();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Register with email/password
  const register = async (email, password, name) => {
    setError(null);
    
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password, name }),
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      const errorMessage = data.detail || 'Registration failed';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
    
    localStorage.setItem('user', JSON.stringify(data));
    setUser(data);
    return data;
  };

  // Login with email/password
  const login = async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email, password }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        const errorMessage = data.detail || 'Login failed';
        setError(errorMessage);
        setLoading(false);
        throw new Error(errorMessage);
      }
      
      localStorage.setItem('user', JSON.stringify(data));
      setUser(data);
      setLoading(false);
      return data;
    } catch (err) {
      setLoading(false);
      throw err;
    }
  };

  // Logout
  const logout = async () => {
    try {
      await fetch(`${API_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      localStorage.removeItem('user');
      setUser(null);
    }
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    register,
    login,
    logout,
    checkAuth,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
